import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Button,
	Snackbar,
	Alert,
	Grid,
	Tabs,
	Tab,
	Paper,
	TextField,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Checkbox,
	IconButton,
	InputAdornment,
	CircularProgress,
	Collapse,
	MenuItem,
	Select,
	Skeleton,
} from "@mui/material";
import { Search, Edit } from "@mui/icons-material";
import axios from "../../../api/axios";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

// Static options
const locationsList = ["Warehouse A", "On Route", "Warehouse B", "Delivered"];

export default function AdvancedShipmentUI() {
	const [shipments, setShipments] = useState([]);
	const [loadingShipments, setLoadingShipments] = useState({});
	const [selectedTrackingCodes, setSelectedTrackingCodes] = useState([]); // switch to trackingCode
	const [searchTerm, setSearchTerm] = useState("");
	const [category, setCategory] = useState("All");
	const [tabIndex, setTabIndex] = useState(0);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [expandedId, setExpandedId] = useState(null);
	const [editDataMap, setEditDataMap] = useState({});
	const [bulkStatus, setBulkStatus] = useState("");
	const [bulkLocation, setBulkLocation] = useState("");

	// Fetch shipments
	useEffect(() => {
		const fetchShipments = async () => {
			try {
				const response = await axios("/all-shipments");
				if (response.status === 200) {
					const data = response.data.shipments;
					const mapped = data.map((item) => ({
						id: item._id,
						senderName: item.senderFullName,
						trackingCode: item.trackingNumber,
						receiverName: item.recipientFullName,
						status: item.shipmentStatus,
						pickupDate: new Date(
							parseInt(item.pickUpDate)
						).toLocaleDateString(),
						deliveryDate: new Date(
							parseInt(item.estimatedDeliveryDate)
						).toLocaleDateString(),
						location:
							item.currentLocation ||
							`${item?.fromCity}  ${item?.fromCountry}` ||
							"Unknown",
						shipmentItems: item.shipmentList,
					}));
					setShipments(mapped);
				}
			} catch (error) {
				console.error(error);
				setNotification({
					open: true,
					message: "Failed to fetch shipments",
					severity: "error",
				});
			}
		};
		fetchShipments();
	}, []);

	const filteredShipments = shipments.filter((s) => {
		const matchesSearch =
			s.senderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			s.receiverName.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesCategory = category === "All" || s.status === category;
		return matchesSearch && matchesCategory;
	});

	// Selection handlers
	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedTrackingCodes(filteredShipments.map((s) => s.trackingCode));
		} else {
			setSelectedTrackingCodes([]);
		}
	};

	const handleSelectOne = (trackingCode) => (e) => {
		if (e.target.checked) {
			setSelectedTrackingCodes((prev) => [...prev, trackingCode]);
		} else {
			setSelectedTrackingCodes((prev) =>
				prev.filter((tc) => tc !== trackingCode)
			);
		}
	};

	// Handlers for search and category
	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const handleCategoryChange = (e, newVal) => {
		const categories = [
			"All",
			"Pending",
			"In Transit",
			"Delivered",
			"Cancelled",
		];
		setCategory(categories[newVal]);
		setTabIndex(newVal);
	};

	// Expand/collapse location edit
	const handleExpand = (trackingCode) => {
		setExpandedId((prev) => (prev === trackingCode ? null : trackingCode));
	};

	// Location change input
	const handleLocationChange = (trackingCode, value) => {
		setEditDataMap((prev) => ({
			...prev,
			[trackingCode]: { ...(prev[trackingCode] || {}), location: value },
		}));
	};

	// Save location update
	const handleSaveLocation = async (trackingCode) => {
		setLoadingShipments((prev) => ({ ...prev, [trackingCode]: true }));
		const shipment = shipments.find((s) => s.trackingCode === trackingCode);
		const newLocation =
			editDataMap[trackingCode]?.location || shipment.location;

		try {
			// API expects trackingNumber, payload with new location
			const response = await axios.patch("/single-location-shipment-update", {
				trackingNumber: trackingCode,
				location: newLocation,
			});
			console.log(response);
			setShipments((prev) =>
				prev.map((s) =>
					s.trackingCode === trackingCode ? { ...s, location: newLocation } : s
				)
			);
			setNotification({
				open: true,
				message: "Location updated",
				severity: "success",
			});
		} catch (error) {
			console.error(error);
			setNotification({
				open: true,
				message: "Error updating location",
				severity: "error",
			});
		} finally {
			setLoadingShipments((prev) => ({ ...prev, [trackingCode]: false }));
			setEditDataMap((prev) => {
				const newMap = { ...prev };
				delete newMap[trackingCode];
				return newMap;
			});
			setExpandedId(null);
		}
	};

	// Validation
	const handleValidateShipment = (s) => {
		const errors = {};
		if (!s.senderName) errors.senderName = "Sender name required";
		if (!s.receiverName) errors.receiverName = "Receiver name required";
		if (!s.pickupDate) errors.pickupDate = "Pickup date required";
		if (!s.deliveryDate) errors.deliveryDate = "Delivery date required";
		if (new Date(s.pickupDate) > new Date(s.deliveryDate))
			errors.deliveryDate = "Must be after pickup date";
		return errors;
	};

	// Save shipment details
	const handleSaveShipment = async (trackingNumber) => {
		const shipment = shipments.find((s) => s.trackingCode === trackingNumber);
		const s = { ...shipment, ...editDataMap[trackingNumber] };
		const errors = handleValidateShipment(s);
		if (Object.keys(errors).length > 0) {
			setNotification({
				open: true,
				message: "Fix validation errors",
				severity: "error",
			});
			return;
		}
		setLoadingShipments((prev) => ({ ...prev, [trackingNumber]: true }));
		try {
			// API expects trackingNumber for update
			await axios.patch("/api/shipments/", s);
			setShipments((prev) =>
				prev.map((ship) => (ship.trackingCode === trackingNumber ? s : ship))
			);
			setNotification({
				open: true,
				message: "Shipment saved",
				severity: "success",
			});
		} catch (error) {
			console.error(error);
			setNotification({
				open: true,
				message: "Error saving shipment",
				severity: "error",
			});
		} finally {
			setLoadingShipments((prev) => ({ ...prev, [trackingNumber]: false }));
			setEditDataMap((prev) => {
				const newMap = { ...prev };
				delete newMap[trackingNumber];
				return newMap;
			});
		}
	};

	// Bulk status update
	const handleBulkStatusUpdate = async () => {
		if (!bulkStatus) {
			setNotification({
				open: true,
				message: "Select a status",
				severity: "error",
			});
			return;
		}
		try {
			console.log(bulkStatus);
			const response = await axios.patch("/bulk-status-update", {
				trackingNumbers: selectedTrackingCodes,
				status: bulkStatus,
			});
			console.log(response);
			setShipments((prev) =>
				prev.map((s) =>
					selectedTrackingCodes.includes(s.trackingCode)
						? { ...s, status: bulkStatus }
						: s
				)
			);
			setNotification({
				open: true,
				message: `Status set to ${bulkStatus}`,
				severity: "success",
			});
		} catch (error) {
			console.error(error);
			setNotification({
				open: true,
				message: "Bulk update failed",
				severity: "error",
			});
		}
	};

	// Bulk location update
	const handleMassLocationUpdate = async () => {
		if (!bulkLocation) {
			setNotification({
				open: true,
				message: "Select a location",
				severity: "error",
			});
			return;
		}
		try {
			const response = await axios.patch("/bulk-location-update", {
				trackingNumbers: selectedTrackingCodes,
				location: bulkLocation,
			});

			console.log(response);
			setShipments((prev) =>
				prev.map((s) =>
					selectedTrackingCodes.includes(s.trackingCode)
						? { ...s, location: bulkLocation }
						: s
				)
			);
			setNotification({
				open: true,
				message: `Location set to ${bulkLocation}`,
				severity: "success",
			});
		} catch (error) {
			console.error(error);
			setNotification({
				open: true,
				message: "Bulk location update failed",
				severity: "error",
			});
		}
	};

	// Update selected fields
	const handleUpdateSelectedFields = async () => {
		if (selectedTrackingCodes.length === 0) {
			setNotification({
				open: true,
				message: "No shipment selected",
				severity: "info",
			});
			return;
		}
		const updatePromises = selectedTrackingCodes.map(async (trackingCode) => {
			const shipment = shipments.find((s) => s.trackingCode === trackingCode);
			const updatedData = { ...shipment };
			if (bulkStatus) updatedData.status = bulkStatus;
			if (bulkLocation) updatedData.location = bulkLocation;
			const response = await axios.patch("/bulk-update", {
				trackingNumber: trackingCode,
				...updatedData,
			});

			console.log(response);
		});
		try {
			await Promise.all(updatePromises);
			setShipments((prev) =>
				prev.map((s) => {
					if (selectedTrackingCodes.includes(s.trackingCode)) {
						const updated = { ...s };
						if (bulkStatus) updated.status = bulkStatus;
						if (bulkLocation) updated.location = bulkLocation;
						return updated;
					}
					return s;
				})
			);
			setNotification({
				open: true,
				message: "Fields updated",
				severity: "success",
			});
		} catch (error) {
			console.error(error);
			setNotification({
				open: true,
				message: "Update failed",
				severity: "error",
			});
		}
	};

	// Detect initial loading state
	const isLoadingInitial =
		shipments.length === 0 && Object.keys(loadingShipments).length === 0;

	return (
		<Box sx={{ p: 3, maxWidth: "1400px", mx: "auto" }}>
			{/* Search & Tabs */}
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					mb: 2,
					gap: 2,
				}}>
				<TextField
					size="small"
					placeholder="Search..."
					value={searchTerm}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search />
							</InputAdornment>
						),
					}}
					sx={{ minWidth: 250, flex: 1 }}
				/>
				<Tabs
					value={tabIndex}
					onChange={handleCategoryChange}
					indicatorColor="primary"
					textColor="primary">
					<Tab label="All" />
					<Tab label="Pending" />
					<Tab label="In Transit" />
					<Tab label="Delivered" />
					<Tab label="Cancelled" />
				</Tabs>
			</Box>

			{/* Bulk actions */}
			{selectedTrackingCodes.length > 0 && (
				<Box
					sx={{
						mb: 2,
						display: "flex",
						flexWrap: "wrap",
						gap: 2,
						alignItems: "center",
					}}>
					<Select
						value={bulkStatus}
						onChange={(e) => setBulkStatus(e.target.value)}
						displayEmpty
						sx={{ minWidth: 150 }}>
						<MenuItem value="" disabled>
							Select Status
						</MenuItem>
						<MenuItem value="Pending">Pending</MenuItem>
						<MenuItem value="In Transit">In Transit</MenuItem>
						<MenuItem value="Delivered">Delivered</MenuItem>
						<MenuItem value="Cancelled">Cancelled</MenuItem>
					</Select>
					<Select
						value={bulkLocation}
						onChange={(e) => setBulkLocation(e.target.value)}
						displayEmpty
						sx={{ minWidth: 150 }}>
						<MenuItem value="" disabled>
							Select Location
						</MenuItem>
						{locationsList.map((loc) => (
							<MenuItem key={loc} value={loc}>
								{loc}
							</MenuItem>
						))}
					</Select>
					<Button
						variant="contained"
						onClick={handleBulkStatusUpdate}
						disabled={Object.values(loadingShipments).some((l) => l)}>
						<LocalShippingIcon sx={{ mr: "0.5rem" }} /> Set Status for Selected
					</Button>
					<Button
						variant="contained"
						onClick={handleMassLocationUpdate}
						disabled={Object.values(loadingShipments).some((l) => l)}>
						<EditLocationAltIcon sx={{ mr: "0.5rem" }} /> Set Location for
						Selected
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleUpdateSelectedFields}
						disabled={Object.values(loadingShipments).some((l) => l)}>
						<TipsAndUpdatesIcon sx={{ mr: "0.5rem" }} /> Update Selected Fields
					</Button>
				</Box>
			)}

			{/* Shipments table */}
			<Paper sx={{ overflowX: "auto", width: "100%" }}>
				<Table size="small" sx={{ minWidth: 650 }}>
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox">
								<Checkbox
									indeterminate={
										selectedTrackingCodes.length > 0 &&
										selectedTrackingCodes.length < filteredShipments.length
									}
									checked={
										filteredShipments.length > 0 &&
										selectedTrackingCodes.length === filteredShipments.length
									}
									onChange={handleSelectAll}
								/>
							</TableCell>
							<TableCell>S/R</TableCell>
							<TableCell>Tracking code</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Pickup</TableCell>
							<TableCell>Delivery</TableCell>
							<TableCell>Location</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoadingInitial
							? Array.from({ length: 5 }).map((_, index) => (
									<TableRow key={index}>
										<TableCell padding="checkbox">
											<Skeleton variant="circular" width={24} height={24} />
										</TableCell>
										<TableCell>
											<Skeleton width="80%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="60%" />
										</TableCell>
										<TableCell>
											<Skeleton width="70%" />
										</TableCell>
										<TableCell>
											<Skeleton width="70%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
									</TableRow>
							  ))
							: filteredShipments.map((s) => (
									<React.Fragment key={s.trackingCode}>
										<TableRow hover>
											<TableCell padding="checkbox">
												<Checkbox
													checked={selectedTrackingCodes.includes(
														s.trackingCode
													)}
													onChange={handleSelectOne(s.trackingCode)}
												/>
											</TableCell>
											<TableCell>
												{s.senderName}/{s.receiverName}
											</TableCell>
											<TableCell>{s.trackingCode}</TableCell>
											<TableCell>{s.status}</TableCell>
											<TableCell>{s.pickupDate}</TableCell>
											<TableCell>{s.deliveryDate}</TableCell>
											{/* Location Inline Edit */}
											<TableCell>
												{expandedId === s.trackingCode ? (
													<Grid container alignItems="center" spacing={1}>
														<Grid item xs={8}>
															<TextField
																value={
																	editDataMap[s.trackingCode]?.location ||
																	s.location
																}
																onChange={(e) =>
																	handleLocationChange(
																		s.trackingCode,
																		e.target.value
																	)
																}
																fullWidth
															/>
														</Grid>
														<Grid item xs={4} sx={{ display: "flex", gap: 1 }}>
															<Button
																size="small"
																variant="outlined"
																onClick={() =>
																	handleSaveLocation(s.trackingCode)
																}
																disabled={loadingShipments[s.trackingCode]}>
																{loadingShipments[s.trackingCode] ? (
																	<CircularProgress size={20} />
																) : (
																	"Save"
																)}
															</Button>
															<Button
																size="small"
																variant="outlined"
																onClick={() => {
																	setExpandedId(null);
																	setEditDataMap((prev) => {
																		const newMap = { ...prev };
																		delete newMap[s.trackingCode];
																		return newMap;
																	});
																}}>
																Cancel
															</Button>
														</Grid>
													</Grid>
												) : (
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "space-between",
														}}>
														<Typography variant="body2">
															{s.location}
														</Typography>
														<IconButton
															size="small"
															onClick={() => setExpandedId(s.trackingCode)}>
															<Edit />
														</IconButton>
													</Box>
												)}
											</TableCell>
										</TableRow>
										<Collapse
											in={expandedId === s.trackingCode}
											timeout="auto"
											unmountOnExit>
											<Box
												sx={{
													p: 2,
													bgcolor: "#f0f0f0",
													borderRadius: 1,
													mb: 2,
												}}>
												{/* Additional inline edit fields can go here */}
											</Box>
										</Collapse>
									</React.Fragment>
							  ))}
					</TableBody>
				</Table>
			</Paper>

			{/* Snackbar notifications */}
			<Snackbar
				open={notification.open}
				autoHideDuration={3000}
				onClose={() => setNotification((prev) => ({ ...prev, open: false }))}>
				<Alert severity={notification.severity}>{notification.message}</Alert>
			</Snackbar>
		</Box>
	);
}
